import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { emptySetup, type SetupInput, type SetupView } from '@qm/contracts/customer-setup';
import { Button, Field, Heading, Panel, Text, TextInput, Select } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { bff, BffError } from '../../../lib/bff';
import { CONNECTABLE, safeAuthorizeUrl } from '../integrations/IntegrationsPage';
import { ConnectionGroups } from '../integrations/ConnectionGroups';
import type { ConnectionFamily, ConnectionChannel } from '@qm/contracts/connection-groups';
import { PhoneVerification } from './PhoneVerification';
import { setupCopy } from './copy';
import './setup.css';
const steps = ['business', 'goals', 'accounts', 'brief', 'review'] as const;
const names: Record<string, string> = {
  meta: 'Meta Ads',
  facebook: 'Facebook Pages',
  instagram: 'Instagram',
  google_ads: 'Google Ads',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  snap: 'Snapchat',
  gbp: 'Google Business Profile',
  google_search_console: 'Google Search Console',
};
const editable = (s: SetupView): SetupInput => ({
  revision: s.revision,
  mode: s.mode ?? 'guided',
  step: s.step,
  profile: s.profile,
  goals: s.goals,
  manualSources: s.manualSources,
  brief: s.brief,
});
export function SetupPage() {
  const { locale, me } = useApp(),
    t = setupCopy(locale);
  const [server, setServer] = useState<SetupView | null>(null),
    [form, setForm] = useState<SetupInput>(emptySetup()),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [saved, setSaved] = useState(false),
    [reminded, setReminded] = useState(false),
    [connections, setConnections] = useState<{ provider: string; status: string }[]>([]);
  const [social,setSocial]=useState<{provider:string;status:string}[]|null>(null);
  const [connectionsKnown,setConnectionsKnown]=useState(false);
  const tenantId = me?.tenantId ?? '';
  const tenantRef = useRef(tenantId);
  tenantRef.current = tenantId;
  const generation = useRef(0);
  const locked = useRef(false);
  const [loadedTenant, setLoadedTenant] = useState('');
  const load = () => {
    const epoch = ++generation.current;
    const expectedTenant = tenantRef.current;
    const current = () => generation.current === epoch && tenantRef.current === expectedTenant;
    setServer(null); setLoadedTenant(''); setForm(emptySetup()); setConnections([]); setSocial(null); setConnectionsKnown(false);
    setBusy(false); locked.current = false; setSaved(false); setReminded(false); setError('');
    if (!expectedTenant) return;
    const headers = { 'x-tenant-id': expectedTenant };
    void bff<SetupView>('/v1/identity/setup', { headers }).then(s => {
      if (!current()) return;
      setServer(s); setForm(editable(s)); setLoadedTenant(expectedTenant);
    }, () => { if (current()) setError(t.error); });
    void bff<{provider:string;status:string}[]>('/v1/connectors/connections', {headers})
      .then(s => { if (current()) {setConnections(s);setConnectionsKnown(true);} }, () => undefined);
    void bff<{provider:string;status:string}[]>('/v1/social/accounts',{headers}).then(s=>{if(current())setSocial(s);},()=>undefined);
  };
  useEffect(() => {
    load();
    return () => { generation.current++; };
  }, [tenantId]);
  // Bind every setup request to the workspace from which this form was loaded.
  // The API's existing tenant-header guard rejects stale cross-tab session changes.
  const request = async <T,>(path: `/v1/${string}`, method: 'POST'|'PUT', body: unknown): Promise<T> => {
    const expectedTenant = loadedTenant, epoch = generation.current;
    if (!expectedTenant || expectedTenant !== tenantRef.current) throw new Error('Workspace changed');
    const result = await bff<T>(path, {method, body:JSON.stringify(body), headers:{'x-tenant-id':expectedTenant}});
    if (generation.current !== epoch || tenantRef.current !== expectedTenant) throw new Error('Workspace changed');
    return result;
  };
  const canEdit = !!me?.roles.some((r) => r === 'customer_owner' || r === 'customer_admin');
  const save = async (patch: Partial<SetupInput> = {}) => {
    const next = { ...form, ...patch };
    if (next.profile.website && !/^https:\/\//i.test(next.profile.website))
      next.profile = { ...next.profile, website: `https://${next.profile.website}` };
    const result = await request<SetupView>('/v1/identity/setup', 'PUT', next);
    setServer(result);
    setForm(editable(result));
    setSaved(true);
    return result;
  };
  const run = async (fn: () => Promise<void>) => {
    if (locked.current || loadedTenant !== tenantRef.current) return;
    const epoch = generation.current;
    locked.current = true;
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      await fn();
    } catch (e) {
      if (epoch !== generation.current) return;
      setError(e instanceof BffError && e.status === 409 ? t.conflict : t.error);
    } finally {
      if (epoch === generation.current) { locked.current = false; setBusy(false); }
    }
  };
  const go = (step: SetupInput['step']) =>
    run(async () => {
      await save({ step });
      window.scrollTo({ top: 0, behavior: 'auto' });
    });
  const choose = (mode: 'guided' | 'manual') =>
    run(async () => {
      await save({ mode, step: mode === 'guided' ? 'business' : 'welcome' });
      if (mode === 'manual') window.location.assign('/');
    });
  const later = () =>
    run(async () => {
      await save();
      window.location.assign('/');
    });
  const connect = (provider: string) =>
    run(async () => {
      await save({ step: 'accounts' });
      try {
        const response = await request<{ authorizeUrl: string }>(
          '/v1/connectors/oauth/start',
          'POST',
          { provider },
        );
        const url = safeAuthorizeUrl(response.authorizeUrl);
        if (!url) throw Error();
        window.location.assign(url);
      } catch {
        setError(t.connectionError);
      }
    });
  const connectGroup = (family:ConnectionFamily,channels:ConnectionChannel[]) => run(async()=>{
    await save({step:'accounts'});
    try {
      const r=await request<{authorizeUrl:string}>('/v1/connectors/oauth/group/start','POST',{family,channels,returnTo:'/setup'});
      const url=safeAuthorizeUrl(r.authorizeUrl);if(!url)throw new Error('Invalid authorization URL');window.location.assign(url);
    } catch {setError(t.connectionError);}
  });
  const external = (path: '/content/assets' | '/knowledge' | '/integrations') =>
    run(async () => {
      await save();
      window.location.assign(path);
    });
  const finish = () =>
    run(async () => {
      if (!form.profile.businessName.trim() || !form.goals.length) {
        setError(t.needGoals);
        return;
      }
      const result = await save({ step: 'review' });
      const completed = await request<SetupView>('/v1/identity/setup/complete', 'POST', {
        revision: result.revision,
      });
      setServer(completed);
      setForm(editable(completed));
    });
  const reminder = () =>
    run(async () => {
      await save();
      await request('/v1/identity/setup/reminder', 'POST', {});
      setReminded(true);
    });
  const profile = (key: keyof SetupInput['profile'], label: string, required = false) => (
    <Field label={label}>
      {({ id }) => (
        <TextInput
          id={id}
          value={String(form.profile[key])}
          required={required}
          maxLength={key === 'website' ? 2048 : 150}
          onChange={(e) =>
            setForm((f) => ({ ...f, profile: { ...f.profile, [key]: e.target.value } }))
          }
        />
      )}
    </Field>
  );
  const note = (key: keyof SetupInput['brief'], label: string, max: number) => (
    <Field label={label}>
      {({ id }) => (
        <textarea
          className="qm-setup__textarea"
          id={id}
          maxLength={max}
          value={form.brief[key]}
          onChange={(e) => setForm((f) => ({ ...f, brief: { ...f.brief, [key]: e.target.value } }))}
        />
      )}
    </Field>
  );
  const current = steps.indexOf(form.step as (typeof steps)[number]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (current >= 0 && current < steps.length - 1) void go(steps[current + 1]!);
  };
  return (
    <section className="qm-page qm-setup" aria-labelledby="page-title" data-testid="customer-setup">
      <header className="qm-setup__head">
        <p className="qm-setup__eyebrow">
          Quixo Marketing / {locale === 'ar' ? 'إعداد العميل' : 'Customer setup'}
        </p>
        <h1 className="qm-setup__title" id="page-title">
          {t.title}
        </h1>
        <Text>{t.intro}</Text>
      </header>
      {me && canEdit && me.mfa.state !== 'verified' ? (
        <Panel>
          <Heading level={2}>{t.security}</Heading>
          <Text>{t.securityHint}</Text>
          <Link to="/signin?next=%2Fsetup">{t.securityLink}</Link>
        </Panel>
      ) : !server || loadedTenant !== tenantId ? (
        <>
          <Text role="status">{error || t.loading}</Text>
          {error ? <Button onClick={load}>{t.reload}</Button> : null}
        </>
      ) : !canEdit ? (
        <Text>
          {locale === 'ar'
            ? 'يُدير مالك مساحة العمل أو المسؤول هذا الإعداد.'
            : 'Your workspace owner or administrator manages this setup.'}
        </Text>
      ) : me?.mfa.state !== 'verified' ? (
        <Panel>
          <Heading level={2}>{t.security}</Heading>
          <Text>{t.securityHint}</Text>
          <Link to="/signin?next=%2Fsetup">{t.securityLink}</Link>
        </Panel>
      ) : server.phone.required && !server.phone.verified ? (
        <PhoneVerification onVerified={load} />
      ) : server.completedAt ? (
        <Panel>
          <Heading level={2}>{t.completed}</Heading>
          <Text>{t.approve}</Text>
          <Link to="/">{t.dashboard}</Link>
          <Button
            onClick={() =>
              void run(async () => {
                await save({ step: 'business' });
              })
            }
          >
            {t.save}
          </Button>
        </Panel>
      ) : (
        <>
          {form.step !== 'welcome' ? (
            <ol className="qm-setup__progress" aria-label={t.title}>
              {steps.map((s, i) => (
                <li key={s} aria-current={form.step === s ? 'step' : undefined}>
                  {i + 1}. {t[s]}
                </li>
              ))}
            </ol>
          ) : null}
          <Panel>
            <Heading level={2}>{t[form.step]}</Heading>
            {form.step === 'welcome' ? (
              <div className="qm-setup__tiles">
                <article className="qm-setup__tile">
                  <Heading level={3}>{t.guided}</Heading>
                  <Text>{t.guidedHint}</Text>
                  <Button variant="primary" disabled={busy} onClick={() => void choose('guided')}>
                    {t.guided}
                  </Button>
                </article>
                <article className="qm-setup__tile">
                  <Heading level={3}>{t.manual}</Heading>
                  <Text>{t.manualHint}</Text>
                  <Button disabled={busy} onClick={() => void choose('manual')}>
                    {t.manual}
                  </Button>
                </article>
              </div>
            ) : (
              <form onSubmit={submit} className="qm-setup__form">
                <fieldset disabled={busy} className="qm-setup__fields"><legend className="qm-sr-only">{t[form.step]}</legend>
                {form.step === 'business' ? (
                  <>
                    <div className="qm-setup__grid">
                      {profile('businessName', t.name, true)}
                      {profile('industry', t.industry)}
                      {profile('website', t.website)}
                      {profile('market', t.market)}
                      <Field label={t.timezone}>
                        {({ id }) => (
                          <Select
                            id={id}
                            value={form.profile.timeZone}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                profile: { ...f.profile, timeZone: e.target.value },
                              }))
                            }
                          >
                            {Array.from(
                              new Set([
                                form.profile.timeZone,
                                'Asia/Dubai',
                                'Asia/Riyadh',
                                'Asia/Kolkata',
                                'Europe/London',
                                'America/New_York',
                                'America/Los_Angeles',
                                'UTC',
                              ]),
                            ).map((v) => (
                              <option value={v} key={v}>
                                {v}
                              </option>
                            ))}
                          </Select>
                        )}
                      </Field>
                    </div>
                    <fieldset>
                      <legend>{t.languages}</legend>
                      {(['en', 'ar'] as const).map((l) => (
                        <label className="qm-setup__check" key={l}>
                          <input
                            type="checkbox"
                            checked={form.profile.languages.includes(l)}
                            disabled={
                              form.profile.languages.length === 1 &&
                              form.profile.languages.includes(l)
                            }
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                profile: {
                                  ...f.profile,
                                  languages: e.target.checked
                                    ? [...f.profile.languages, l]
                                    : f.profile.languages.filter((v) => v !== l),
                                },
                              }))
                            }
                          />
                          {l === 'en' ? 'English' : 'العربية'}
                        </label>
                      ))}
                    </fieldset>
                  </>
                ) : null}
                {form.step === 'goals' ? (
                  <>
                    <Text>{t.selectGoals}</Text>
                    <div className="qm-setup__tiles">
                      {(['leads', 'bookings', 'sales', 'visibility', 'retention'] as const).map(
                        (goal) => (
                          <label
                            className="qm-setup__tile qm-setup__check"
                            data-active={form.goals.includes(goal)}
                            key={goal}
                          >
                            <input
                              type="checkbox"
                              checked={form.goals.includes(goal)}
                              disabled={!form.goals.includes(goal) && form.goals.length >= 2}
                              onChange={(e) =>
                                setForm((f) => ({
                                  ...f,
                                  goals: e.target.checked
                                    ? [...f.goals, goal]
                                    : f.goals.filter((v) => v !== goal),
                                }))
                              }
                            />
                            {t[goal]}
                          </label>
                        ),
                      )}
                    </div>
                  </>
                ) : null}
                {form.step === 'accounts' ? (
                  <>
                    <Text>{t.connectHint}</Text>
                    <ConnectionGroups locale={locale} busy={busy} connections={connectionsKnown?connections:null} social={social} onConnect={(family,channels)=>void connectGroup(family,channels)}/>
                    <div className="qm-setup__tiles qm-setup__accounts">
                      {CONNECTABLE.filter(p=>!['meta','google_ads','gbp','google_search_console'].includes(p)).map((p) => (
                        <article className="qm-setup__tile" key={p}>
                          <Heading level={3}>{names[p]}</Heading>
                          <Text tone="muted">
                            {connections.some((c) => c.provider === p && c.status === 'connected')
                              ? t.connected
                              : connections.some((c) => c.provider === p)
                                ? t.attention
                                : t.unconnected}
                          </Text>
                          <Button type="button" disabled={busy} onClick={() => void connect(p)}>
                            {t.connect}
                          </Button>
                        </article>
                      ))}
                    </div>
                    <Text>{t.noChanges}</Text>
                    <Heading level={3}>{t.manualData}</Heading>
                    <Text>{t.manualHintData}</Text>
                    {form.manualSources.map((source, i) => (
                      <fieldset key={i}>
                        <legend>
                          {t.sourceName} {i + 1}
                        </legend>
                        {(['name', 'url', 'notes'] as const).map((k) => (
                          <Field
                            key={k}
                            label={
                              k === 'name' ? t.sourceName : k === 'url' ? t.sourceUrl : t.notes
                            }
                          >
                            {({ id }) => (
                              <TextInput
                                id={id}
                                value={source[k]}
                                maxLength={k === 'notes' ? 2000 : k === 'name' ? 150 : 2048}
                                onChange={(e) =>
                                  setForm((f) => ({
                                    ...f,
                                    manualSources: f.manualSources.map((s, n) =>
                                      n === i ? { ...s, [k]: e.target.value } : s,
                                    ),
                                  }))
                                }
                              />
                            )}
                          </Field>
                        ))}
                        <Button
                          type="button"
                          onClick={() =>
                            setForm((f) => ({
                              ...f,
                              manualSources: f.manualSources.filter((_, n) => n !== i),
                            }))
                          }
                        >
                          {t.remove}
                        </Button>
                      </fieldset>
                    ))}
                    <Button
                      type="button"
                      disabled={form.manualSources.length >= 12}
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          manualSources: [...f.manualSources, { name: '', url: '', notes: '' }],
                        }))
                      }
                    >
                      {t.addSource}
                    </Button>
                    <div className="qm-setup__links">
                      <Button type="button" onClick={() => void external('/content/assets')}>
                        {t.import}
                      </Button>
                      <Button type="button" onClick={() => void external('/integrations')}>
                        {t.integrations}
                      </Button>
                    </div>
                    <Text>{t.importHint}</Text>
                    <Heading level={3}>{t.waBusiness}</Heading>
                    <Text>{t.waBusinessHint}</Text>
                    <Text tone="muted">
                      {locale === 'ar'
                        ? 'يلزم إعداد حساب الأعمال وأذوناته بصورة منفصلة.'
                        : 'Business account authorization must be completed separately; personal verification is not a connection.'}
                    </Text>
                  </>
                ) : null}
                {form.step === 'brief' ? (
                  <>
                    {note('services', t.services, 3000)}
                    {note('audience', t.audience, 1500)}
                    {note('offer', t.offer, 1500)}
                    {note('tone', t.tone, 300)}
                    <Button type="button" onClick={() => void external('/knowledge')}>
                      {t.knowledge}
                    </Button>
                    <Text>{t.importHint}</Text>
                  </>
                ) : null}
                {form.step === 'review' ? (
                  <>
                    <Text>{t.reviewHint}</Text>
                    <dl className="qm-setup__summary">
                      <dt>{t.name}</dt>
                      <dd>{form.profile.businessName || t.empty}</dd>
                      <dt>{t.goals}</dt>
                      <dd>{form.goals.map((g) => t[g]).join(' · ') || t.empty}</dd>
                      <dt>{t.accounts}</dt>
                      <dd>
                        {connections.length
                          ? connections
                              .map(
                                (c) =>
                                  `${names[c.provider] ?? c.provider}: ${c.status === 'connected' ? t.connected : t.attention}`,
                              )
                              .join(' · ')
                          : t.unconnected}
                      </dd>
                      <dt>{t.manualData}</dt>
                      <dd>
                        {form.manualSources.length} — {t.manualHintData}
                      </dd>
                      <dt>{t.brief}</dt>
                      <dd>{form.brief.services || t.empty}</dd>
                    </dl>
                    <Heading level={3}>{t.draftTitle}</Heading>
                    <ol>
                      <li>{t.draft1}</li>
                      <li>{t.draft2}</li>
                      <li>{t.draft3}</li>
                    </ol>
                    <p className="qm-setup__note">{t.approve}</p>
                    <Button
                      type="button"
                      variant="primary"
                      disabled={busy}
                      onClick={() => void finish()}
                    >
                      {t.finish}
                    </Button>
                  </>
                ) : null}
                <div className="qm-setup__actions">
                  <Button
                    type="button"
                    disabled={busy}
                    onClick={() => void go(current > 0 ? steps[current - 1]! : 'welcome')}
                  >
                    {t.back}
                  </Button>
                  {form.step !== 'review' ? (
                    <Button type="submit" variant="primary" disabled={busy}>
                      {t.continue}
                    </Button>
                  ) : null}
                  <Button type="button" disabled={busy} onClick={() => void later()}>
                    {t.later}
                  </Button>
                </div>
                </fieldset>
              </form>
            )}
            {saved ? <Text role="status">{t.saved}</Text> : null}
            {error ? (
              <div role="alert">
                <Text>{error}</Text>
                <Button onClick={load}>{t.reload}</Button>
              </div>
            ) : null}
          </Panel>
          <div className="qm-setup__actions">
            <Button disabled={busy} onClick={() => void reminder()}>
              {t.remind}
            </Button>
            <Link to="/">{t.dashboard}</Link>
          </div>
          {reminded ? <Text role="status">{t.reminded}</Text> : null}
          {!server.phone.required && (form.step === 'welcome' || form.step === 'review') ? (
            <Panel>
              <PhoneVerification onVerified={load} />
            </Panel>
          ) : null}
        </>
      )}
    </section>
  );
}
export function SetupChecklist() {
  const { locale, me } = useApp(),
    t = setupCopy(locale);
  const [state, setState] = useState<SetupView | null>(null);
  useEffect(() => {
    setState(null);
    if (!me?.tenantId || !me.roles.some((r) => r === 'customer_owner' || r === 'customer_admin'))
      return;
    let live = true;
    void bff<SetupView>('/v1/identity/setup', {headers:{'x-tenant-id':me.tenantId}}).then(
      (s) => {
        if (!live) return;
        setState(s);
        if (s.shouldPrompt || (s.phone.required && !s.phone.verified))
          window.location.replace('/setup');
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [me?.tenantId]);
  if (!state || state.completedAt) return null;
  return (
    <aside className="qm-setup__card" aria-labelledby="setup-checklist">
      <Heading level={2} id="setup-checklist">
        {t.checklist}
      </Heading>
      <Text>{state.mode === 'manual' ? t.manualStatus : t.guidedHint}</Text>
      <ul>
        <li>
          {t.business}: {state.profile?.businessName || t.empty}
        </li>
        <li>
          {t.goals}: {state.goals.length ? state.goals.map((g) => t[g]).join(' · ') : t.empty}
        </li>
        <li>
          {t.accounts}: {t.optional}
        </li>
        <li>
          {t.brief}: {state.brief.services ? t.saved : t.empty}
        </li>
      </ul>
      <Link to="/setup">{t.resume}</Link>
    </aside>
  );
}
